import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { SideMsgDockContext } from '#/modules/commonComponents/SideMsgDockContext';
import {
  readSideMsgPrefs,
  SIDE_MSG_DOCK_MODE,
  writeSideMsgPrefs,
} from '#/utils/userSideMsgPrefsStorage';

const PINNED_GRID_COLS = 'xl:grid-cols-[1fr_16rem] 2xl:grid-cols-[1fr_22.5rem]';
const XL_MEDIA_QUERY = '(min-width: 80rem)';
const ADMIN_CONTENT_SELECTOR = 'main.admin-layout__content';
const TONGUE_TOP_REM = 2.5;

const DOCK_EDGE_BTN_CLASS = cn(
  'py-5',
  'size-auto h-16 w-7',
  'bg-panel',
  'rounded-l-md! rounded-r-none!',
  'border border-r-0 border-gray-300',
  'hidden xl:flex',
  'absolute',
  'z-[var(--z-side-msg-float)]',
  'hover:bg-panel',
);

/**
 * 不 flatten Fragment，避免業務 SideMsg 薄包（ConfirmDialogHost + SideMsg）被拆成多欄。
 * @param {import('react').ReactNode} children
 * @returns {import('react').ReactNode[]}
 */
function getChildList(children) {
  if (children == null || children === false) {
    return [];
  }

  return Array.isArray(children) ? children : [children];
}

function readInitialMode(enableDockModes) {
  if (!enableDockModes) {
    return SIDE_MSG_DOCK_MODE.PINNED;
  }

  return readSideMsgPrefs()?.mode ?? SIDE_MSG_DOCK_MODE.PINNED;
}

function readInitialCollapsed(enableDockModes) {
  if (!enableDockModes) {
    return false;
  }

  const prefs = readSideMsgPrefs();
  if (!prefs || prefs.mode !== SIDE_MSG_DOCK_MODE.FLOATING) {
    return false;
  }

  return Boolean(prefs.floatingCollapsed);
}

function isXlViewport() {
  return window.matchMedia(XL_MEDIA_QUERY).matches;
}

function hasOpenModalDialog() {
  return Boolean(
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
    ),
  );
}

function getEventElement(target) {
  if (target instanceof Element) {
    return target;
  }

  if (target instanceof Node) {
    return target.parentElement;
  }

  return null;
}

function shouldIgnoreFloatingCollapseTarget(target) {
  const el = getEventElement(target);
  if (!el) {
    return true;
  }

  if (el.closest('[data-side-msg-dock]')) {
    return true;
  }

  if (el.closest('[data-side-msg-expand]')) {
    return true;
  }

  if (el.closest('.table-panel')) {
    return true;
  }

  return false;
}

function measureFloatingDock(mainEl, layoutEl) {
  const tablePanel = layoutEl?.querySelector('.table-panel');
  if (!mainEl || !tablePanel) {
    return { top: 0, height: 0, expandBtnTop: 0 };
  }

  const mainRect = mainEl.getBoundingClientRect();
  const panelRect = tablePanel.getBoundingClientRect();
  const top = Math.max(0, panelRect.top - mainRect.top);
  const height = panelRect.height;
  const rootFont = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

  return {
    top,
    height,
    expandBtnTop: top + (TONGUE_TOP_REM * rootFont),
  };
}

/**
 * L1 — 主內容 + 側欄 grid RWD。
 * 第一個 child 為主區、第二個為 SideMsg（或薄包）。
 * 頁級 `h1.page_title` 應放在本元件外（見 SP-02h）。
 * `< xl` 自然堆疊不搶高；`≥ xl` 齊高（見 SP-02i）。
 * `enableDockModes` 預設 false；僅試點頁傳 true（見 SP-16）。
 *
 * @see docs/UI_LAYOUT.md
 */
const SideMsgLayout = ({ children, className, enableDockModes = false }) => {
  const [mode, setMode] = useState(() => readInitialMode(enableDockModes));
  const [floatingCollapsed, setFloatingCollapsed] = useState(
    () => readInitialCollapsed(enableDockModes),
  );
  const [isXl, setIsXl] = useState(() => isXlViewport());
  const [dockBox, setDockBox] = useState({ top: 0, height: 0, expandBtnTop: 0 });
  const layoutRef = useRef(null);

  const isFloating = mode === SIDE_MSG_DOCK_MODE.FLOATING;
  const isFloatingCollapsed = isFloating && floatingCollapsed;
  const isFloatingExpanded = isFloating && !floatingCollapsed;
  const portalFloatingDock = enableDockModes && isFloating && isXl;
  const portalHost = portalFloatingDock
    ? document.querySelector(ADMIN_CONTENT_SELECTOR)
    : null;

  const persistDock = useCallback((nextMode, nextCollapsed) => {
    const collapsed = nextMode === SIDE_MSG_DOCK_MODE.PINNED ? false : nextCollapsed;
    setMode(nextMode);
    setFloatingCollapsed(collapsed);
    writeSideMsgPrefs({ mode: nextMode, floatingCollapsed: collapsed });
  }, []);

  const toggleMode = useCallback(() => {
    if (mode === SIDE_MSG_DOCK_MODE.PINNED) {
      persistDock(SIDE_MSG_DOCK_MODE.FLOATING, false);
      return;
    }

    persistDock(SIDE_MSG_DOCK_MODE.PINNED, false);
  }, [mode, persistDock]);

  const collapseFloating = useCallback(() => {
    if (mode !== SIDE_MSG_DOCK_MODE.FLOATING) {
      return;
    }

    persistDock(SIDE_MSG_DOCK_MODE.FLOATING, true);
  }, [mode, persistDock]);

  const expandFloating = useCallback(() => {
    persistDock(SIDE_MSG_DOCK_MODE.FLOATING, false);
  }, [persistDock]);

  useEffect(() => {
    const media = window.matchMedia(XL_MEDIA_QUERY);

    function onChange() {
      setIsXl(media.matches);
    }

    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);

  useLayoutEffect(() => {
    if (!enableDockModes || !isFloating) {
      return undefined;
    }

    const mainEl = document.querySelector(ADMIN_CONTENT_SELECTOR);
    if (!mainEl) {
      return undefined;
    }

    function updateGeometry() {
      setDockBox(measureFloatingDock(mainEl, layoutRef.current));
    }

    const observer = new ResizeObserver(updateGeometry);
    observer.observe(mainEl);
    const tablePanel = layoutRef.current?.querySelector('.table-panel');
    if (tablePanel) {
      observer.observe(tablePanel);
    }
    window.addEventListener('resize', updateGeometry);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateGeometry);
    };
  }, [enableDockModes, isFloating, isXl]);

  useEffect(() => {
    if (!enableDockModes || !isFloatingExpanded || !isXl) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key !== 'Escape') {
        return;
      }

      if (!isXlViewport() || hasOpenModalDialog()) {
        return;
      }

      collapseFloating();
    }

    function handlePointerDown(event) {
      if (!isXlViewport() || hasOpenModalDialog()) {
        return;
      }

      if (shouldIgnoreFloatingCollapseTarget(event.target)) {
        return;
      }

      collapseFloating();
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [collapseFloating, enableDockModes, isFloatingExpanded, isXl]);

  const dockValue = useMemo(() => ({
    enableDockModes: true,
    mode,
    floatingCollapsed,
    toggleMode,
    collapseFloating,
    expandFloating,
  }), [mode, floatingCollapsed, toggleMode, collapseFloating, expandFloating]);

  if (!enableDockModes) {
    return (
      <div
        className={cn(
          'side-msg-layout flex w-full flex-none flex-col',
          'xl:min-h-0 xl:flex-1',
          className,
        )}
      >
        <div
          className="
            grid grid-cols-1 gap-2
            md:gap-5
            xl:h-full xl:min-h-0 xl:flex-1 xl:grid-cols-[1fr_16rem] xl:items-stretch
            2xl:grid-cols-[1fr_22.5rem]
          "
        >
          {children}
        </div>
      </div>
    );
  }

  const childList = getChildList(children);
  const main = childList[0];
  const side = childList[1];

  const dock = side == null ? null : (
    <div
      data-side-msg-dock=""
      className={cn(
        'min-h-0',
        !portalFloatingDock && 'xl:h-full',
        portalFloatingDock && cn(
          'absolute right-0',
          'w-[16rem] 2xl:w-[22.5rem]',
          'z-[var(--z-side-msg-float)]',
          'overflow-visible',
          'translate-x-0 shortcut_transition-transform',
          isFloatingCollapsed && 'pointer-events-none translate-x-[calc(100%+1.75rem)]',
        ),
      )}
      style={portalFloatingDock
        ? { top: `${dockBox.top}px`, height: `${dockBox.height}px` }
        : undefined}
    >
      {portalFloatingDock ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="收合側欄"
          className={cn(
            DOCK_EDGE_BTN_CLASS,
            'top-10 left-0 -translate-x-full',
          )}
          onClick={collapseFloating}
        >
          <ChevronRight className="size-4" aria-hidden />
        </Button>
      ) : null}
      {side}
    </div>
  );

  const expandBtn = (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label="展開側欄"
      data-side-msg-expand=""
      className={cn(
        DOCK_EDGE_BTN_CLASS,
        'right-0',
        'shortcut_transition',
        isFloatingCollapsed
          ? 'pointer-events-auto translate-x-0 opacity-100'
          : 'pointer-events-none translate-x-1 opacity-0',
      )}
      style={{ top: `${dockBox.expandBtnTop}px` }}
      onClick={expandFloating}
    >
      <ChevronLeft className="size-4" aria-hidden />
    </Button>
  );

  return (
    <SideMsgDockContext.Provider value={dockValue}>
      <div
        ref={layoutRef}
        className={cn(
          'side-msg-layout flex w-full flex-none flex-col',
          'xl:relative xl:min-h-0 xl:flex-1',
          className,
        )}
      >
        <div
          className={cn(
            'grid grid-cols-1 gap-2 md:gap-5',
            'xl:relative xl:h-full xl:min-h-0 xl:flex-1 xl:items-stretch',
            !isFloating && PINNED_GRID_COLS,
            isFloating && 'xl:grid-cols-1',
          )}
        >
          {main}
          {portalFloatingDock ? null : dock}
        </div>
      </div>
      {portalHost
        ? createPortal(
          <>
            {dock}
            {expandBtn}
          </>,
          portalHost,
        )
        : null}
    </SideMsgDockContext.Provider>
  );
};

SideMsgLayout.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  enableDockModes: PropTypes.bool,
};

export default SideMsgLayout;
